import React, { useState, useEffect } from 'react';
import { withRouter } from '../../common/withRouter';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Notification,
  ButtonOBSOLETED,
  Loader,
  Message
} from '../../web.common';
import withApi from '../higherOrderComponents/withApi';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';

const AreaAddInternal = ({
  api,
  history,
  match
}) => {
  const areaId = match.params.areaId;
  const isEditMode = !!areaId;

  const initialValue = {
    Name: '',
    Slug: '',
    CountryCode: '',
    Latitude: '',
    Longitude: '',
    ZipFrom: '',
    ZipTo: '',
    IsMainArea: false,
    IsSuperArea: false
  };

  const [formData, setFormData] = useState(initialValue);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      loadAreaData();
    }
  }, [areaId]);

  const loadAreaData = async () => {
    try {
      const area = await api.areas.getArea(areaId);
      setFormData({
        Name: area.name || '',
        Slug: area.slug || '',
        CountryCode: area.countryCode || '',
        Latitude: area.position?.latitude || '',
        Longitude: area.position?.longitude || '',
        ZipFrom: area.zipFrom || '',
        ZipTo: area.zipTo || '',
        IsMainArea: area.isMainArea || false,
        IsSuperArea: area.isSuperArea || false
      });
      setIsLoading(false);
    } catch (e) {
      setNotification(new Message('error', 'Unable to load area data'));
      setIsLoading(false);
    }
  };

  const formLabelStyle = {
    display: "table-cell",
    width: "20%",
    whiteSpace: "nowrap",
    fontWeight: "bold",
    verticalAlign: "top"
  };

  const formSpanStyle = {
    display: "table-cell",
  };

  const rowStyle = {
    display: "table",
    width: "100%",
    margin: "auto",
    marginBottom: "0"
  };

  const containerStyle = {
    width: '80%',
    margin: 'auto',
    backgroundColor: 'white',
    border: "1px solid #e5e5e5",
    padding: "1.2rem"
  };

  const validateForm = () => {
    const emptyFields = [];
    let response = {
      success: false,
      type: 'error',
      message: ''
    };

    if (formData.Name.trim() === '') {
      emptyFields.push('Name');
    }
    if (formData.Slug.trim() === '') {
      emptyFields.push('Slug');
    }
    if (formData.CountryCode.trim() === '') {
      emptyFields.push('Country Code');
    }

    if (emptyFields.length === 1) {
      response.message = `Unable to save. The ${emptyFields[0]} field is mandatory.`;
    } else if (emptyFields.length > 1) {
      response.message = `Unable to save. ${emptyFields.join(', ')} are mandatory.`;
    } else {
      response.success = true;
      response.type = 'success';
    }

    return response;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationResponse = validateForm();

    if (!validationResponse.success) {
      setNotification(new Message(validationResponse.type, validationResponse.message));
      return;
    }

    try {
      const payload = {
        Name: formData.Name.trim(),
        Slug: formData.Slug.trim(),
        CountryCode: formData.CountryCode.trim().toUpperCase(),
        Latitude: formData.Latitude ? parseFloat(formData.Latitude) : null,
        Longitude: formData.Longitude ? parseFloat(formData.Longitude) : null,
        ZipFrom: formData.ZipFrom ? parseInt(formData.ZipFrom) : null,
        ZipTo: formData.ZipTo ? parseInt(formData.ZipTo) : null,
        IsMainArea: formData.IsMainArea,
        IsSuperArea: formData.IsSuperArea
      };

      if (isEditMode) {
        const result = await api.areas.updateArea(areaId, payload);
        if (result.success === false) {
          setNotification(new Message('error', result.errorMessage));
          return;
        }
        setNotification(new Message('success', 'Area updated successfully.'));
        setTimeout(() => {
          history.push(`/area/${areaId}`);
        }, 1500);
      } else {
        const result = await api.areas.createArea(payload);
        if (result.success === false) {
          setNotification(new Message('error', result.errorMessage));
          return;
        }
        setNotification(new Message('success', 'Area created successfully.'));
        setTimeout(() => {
          history.push(`/area/${result.areaId}`);
        }, 1500);
      }
    } catch (e) {
      console.error('Error saving area:', e);
      console.error('Error payload:', e.payload);
      console.error('Full error object:', JSON.stringify(e, null, 2));

      // Try to extract error message from various possible locations
      let errorMessage = 'Unable to save area';

      if (e.payload?.translatedMessage) {
        errorMessage = e.payload.translatedMessage;
      } else if (e.payload?.message) {
        errorMessage = e.payload.message;
      } else if (e.message && e.message !== 'Server returned error: ') {
        errorMessage = e.message.replace('Server returned error: ', '');
      }

      setNotification(new Message('error', errorMessage));
    }
  };

  if (isLoading) {
    return <Loader hasLoaded={false}>Loading...</Loader>;
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <PageTop
            documentTitle={isEditMode ? 'Edit Area' : 'New Area'}
            documentDescription={isEditMode ? 'Edit Area' : 'New Area'}
          />
          <React.Fragment>
            <h2 className='main-page-header main-page-header-with-subheader'>
              {isEditMode ? 'Edit Area' : 'New Area'}
            </h2>
            <h5 className='main-page-subheader'>
              <i>{isEditMode ? `Editing ${formData.Name}` : 'Create a new area'}</i>
            </h5>
            <br />
            <div style={containerStyle}>
              <form onSubmit={handleSubmit}>
                <div style={rowStyle}>
                  <label htmlFor="name" style={formLabelStyle}>{"Name*"}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="name"
                      id="name"
                      placeholder='Name'
                      value={formData.Name}
                      onChange={({ target }) => setFormData({ ...formData, Name: target.value })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="slug" style={formLabelStyle}>{'Slug*'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="slug"
                      id="slug"
                      placeholder='Slug (e.g., copenhagen, aarhus)'
                      value={formData.Slug}
                      onChange={({ target }) => setFormData({ ...formData, Slug: target.value })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="countryCode" style={formLabelStyle}>{'Country Code*'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="countryCode"
                      id="countryCode"
                      placeholder='ISO 3166-1 alpha-3 (e.g., DNK, SWE, NOR)'
                      maxLength="3"
                      value={formData.CountryCode}
                      onChange={({ target }) => setFormData({ ...formData, CountryCode: target.value.toUpperCase() })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="latitude" style={formLabelStyle}>{'Latitude'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="latitude"
                      id="latitude"
                      type="number"
                      step="any"
                      placeholder='Latitude'
                      value={formData.Latitude}
                      onChange={({ target }) => setFormData({ ...formData, Latitude: target.value })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="longitude" style={formLabelStyle}>{'Longitude'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="longitude"
                      id="longitude"
                      type="number"
                      step="any"
                      placeholder='Longitude'
                      value={formData.Longitude}
                      onChange={({ target }) => setFormData({ ...formData, Longitude: target.value })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="zipFrom" style={formLabelStyle}>{'Zip From'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="zipFrom"
                      id="zipFrom"
                      type="number"
                      placeholder='Zip Code From'
                      value={formData.ZipFrom}
                      onChange={({ target }) => setFormData({ ...formData, ZipFrom: target.value })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="zipTo" style={formLabelStyle}>{'Zip To'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="zipTo"
                      id="zipTo"
                      type="number"
                      placeholder='Zip Code To'
                      value={formData.ZipTo}
                      onChange={({ target }) => setFormData({ ...formData, ZipTo: target.value })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="isMainArea" style={formLabelStyle}>{'Is Main Area'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="isMainArea"
                      id="isMainArea"
                      type="checkbox"
                      checked={formData.IsMainArea}
                      onChange={({ target }) => setFormData({ ...formData, IsMainArea: target.checked })}
                    />
                  </span>
                </div>
                <hr />
                <div style={rowStyle}>
                  <label htmlFor="isSuperArea" style={formLabelStyle}>{'Is Super Area'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="isSuperArea"
                      id="isSuperArea"
                      type="checkbox"
                      checked={formData.IsSuperArea}
                      onChange={({ target }) => setFormData({ ...formData, IsSuperArea: target.checked })}
                    />
                  </span>
                </div>
                <hr />
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <ButtonOBSOLETED
                    buttonColor="primary"
                    type="submit"
                    buttonContent={isEditMode ? "Update Area" : "Create Area"}
                    ariaLabel={isEditMode ? "Update area" : "Create area"}
                    disabled={false}
                  />
                  <span style={{ marginLeft: '15px' }}>
                    <ButtonOBSOLETED
                      buttonColor="gray"
                      type="button"
                      onClick={() => history.push(isEditMode ? `/area/${areaId}` : '/areas')}
                      buttonContent="Cancel"
                      ariaLabel="Cancel"
                      disabled={false}
                    />
                  </span>
                </div>
              </form>
            </div>
          </React.Fragment>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

AreaAddInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

const AreaAdd = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(AreaAddInternal)
  )
);

AreaAdd.requiresLogin = true;

export default AreaAdd;
